import React, { useState, useRef } from 'react';
import './Finder.css';
import MapSection from './MapSection';
import ResponseSection from './ResponseSection';
import YelpSection from './YelpSection';
import SocialSection from './SocialSection';

const RATE_LIMIT = 100;
const RATE_WINDOW_MS = 60 * 60 * 1000;

function getRateLimitState() {
  const stored = localStorage.getItem('generateRateLimit');
  if (!stored) return { count: 0, windowStart: Date.now() };
  const parsed = JSON.parse(stored);
  if (Date.now() - parsed.windowStart > RATE_WINDOW_MS) {
    return { count: 0, windowStart: Date.now() };
  }
  return parsed;
}

function pickRandom(pool) {
  return pool[Math.floor(Math.random() * pool.length)];
}

function Finder({ onRestaurantFound }) {
  const [restaurant, setRestaurant] = useState(null);
  const [latitude, setLatitude] = useState(40.7484);
  const [longitude, setLongitude] = useState(-73.9857);
  const [showMap, setShowMap] = useState(false);
  const [showLimitPrompt, setShowLimitPrompt] = useState(false);
  const poolRef = useRef(null); // loaded once, reused for every Regenerate

  const selectRestaurant = (pool, rateState) => {
    const doc = pickRandom(pool);
    setRestaurant(doc);
    setLatitude(doc.latitude);
    setLongitude(doc.longitude);
    setShowMap(true);
    onRestaurantFound();

    const updated = { count: rateState.count + 1, windowStart: rateState.windowStart };
    localStorage.setItem('generateRateLimit', JSON.stringify(updated));
  };

  const fetchRestaurant = (rateState) => {
    if (poolRef.current) {
      selectRestaurant(poolRef.current, rateState);
      return;
    }

    fetch(process.env.PUBLIC_URL + '/restaurants.json')
      .then((res) => res.json())
      .then((all) => {
        const withReviews = all.filter((r) => r.reviews?.some((rv) => rv.id));
        poolRef.current = withReviews.length > 0 ? withReviews : all;
        selectRestaurant(poolRef.current, rateState);
      })
      .catch((err) => console.error(err));
  };

  const handleButtonClick = () => {
    const rateState = getRateLimitState();
    if (rateState.count >= RATE_LIMIT) {
      setShowLimitPrompt(true);
      return;
    }
    fetchRestaurant(rateState);
  };

  const handleMapReset = () => {
    setShowMap(false);
    setLatitude(40.7484);
    setLongitude(-73.9857);
  };

  return (
    <div className="app">
      {!restaurant && (
        <header className="app-header">
          <p className="description">Find a place to eat with our Random Restaurant Generator</p>
          <button onClick={handleButtonClick} className="cta-button">Generate</button>
        </header>
      )}

      <MapSection latitude={latitude} longitude={longitude} showMap={showMap} />

      <ResponseSection restaurant={restaurant} />

      <YelpSection restaurant={restaurant} />

      <SocialSection restaurant={restaurant} />

      {restaurant && (
        <div className="regenerate-section">
          <p className="description">Don't like what you see?</p>
          <button
            onClick={() => { handleMapReset(); fetchRestaurant(getRateLimitState()); }}
            className="cta-button"
          >
            Regenerate
          </button>
        </div>
      )}

      {showLimitPrompt && (
        <div className="login-prompt">
          <div className="login-prompt-content">
            <span className="close" onClick={() => setShowLimitPrompt(false)}>&times;</span>
            <h2>Hourly limit reached</h2>
            <p>You've used all {RATE_LIMIT} generations for this hour. Please try again later.</p>
          </div>
        </div>
      )}

      <footer className="footer">
        <p className="footer-text">© 2026 <a href="https://laichi.app/">Láichī</a>. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default Finder;
